<template>
  <div>
    <div>
      <md-field :class="{ 'md-invalid': noHits }">
        <md-textarea
          ref="text"
          :value="text"
          @input="updateText"
          placeholder="メーカーコードを改行区切りで入力してください"
        ></md-textarea>
        <span class="md-error">メーカーが見つかりませんでした</span>
      </md-field>
      <div v-if="makerSearchCount > 0">
        {{ makerSearchCount }}件中 {{ makerFoundCount }}メーカー見つかりました
      </div>
    </div>
    <div class="makers-container">
      <maker-list
        :makers="makers"
        :selected.sync="selected"
        selectable
      />
      <div v-if="makerFountExceedsLimit" class="message-max-count-exceeds">
        検索結果上位{{ maxSearchCount }}件を表示しています
      </div>
      <div class="fetching" v-if="fetching">
        <spinner />
      </div>
    </div>
    <div class="button-group">
      <gl-button
        dense
        primary
        icon-pre="list-add"
        class="button-group__button"
        :disabled="makers.length === 0"
        @click=";(selected = makers), $emit('selected', selected)"
      >
        All
      </gl-button>
      <gl-button
        dense
        primary
        class="button-group__button"
        :disabled="selected.length === 0"
        @click="$emit('selected', selected)"
      >
        <gl-icon-list-add />
      </gl-button>
    </div>
  </div>
</template>

<script>
import { Subject, from, of } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  tap,
  map,
  switchMap,
} from 'rxjs/operators'
import MakerList from '@/components/MakerList.vue'
import Spinner from '@/components/Spinner.vue'

export default {
  components: { MakerList, Spinner },
  props: {
    /**
     * 検索時、最大で表示するメーカー数
     *
     * 検索結果（Array）の先頭x件を表示します。並び順はリポジトリがどの
     * 順番で返すかに依存します。
     */
    maxSearchCount: {
      type: Number,
      default: 100,
    },
  },
  data() {
    return {
      /**
       * 検索テキスト
       */
      text: undefined,
      /**
       * 検索テキスト変更Subject
       */
      textSubject: new Subject(),
      /**
       * 検索中
       */
      fetching: false,
      /**
       * 検索テキストでのヒットなし
       */
      noHits: false,
      /**
       * 検索したメーカー数
       */
      makerFoundCount: 0,
      /**
       * 検索したメーカー数
       */
      makerSearchCount: 0,
      /**
       * 選択肢に表示するメーカー
       */
      makers: [],
      /**
       * 選択されているメーカー
       */
      selected: [],
      /**
       * Snackbarプロパティ
       */
      snackbar: {
        show: false,
        duration: 1500,
        message: '',
      },
    }
  },
  computed: {
    /**
     * 検索した結果が表示最大件数をオーバーしているか
     */
    makerFountExceedsLimit() {
      return this.makerFoundCount > this.maxSearchCount
    },
  },
  methods: {
    /**
     * 入力テキスト変更
     */
    updateText(v) {
      this.text = v
      this.textSubject.next(v)
    },
    /**
     * 入力内容でメーカー検索を行う
     *
     * 最大表示件数までしか検索結果を表示しません。
     */
    async search(v) {
      const codes = v
        .split('\n')
        .filter(v => v)
        .map(v => v.trim())

      const hits = await this.$maker.getByCode(codes)

      this.noHits = hits.length === 0
      this.makerSearchCount = codes.length
      this.makerFoundCount = hits.length
      this.makers = hits.slice(0, this.maxSearchCount)
    },
    /**
     * Snackbarの表示
     */
    showSnackbar(options) {
      this.snackbar.duration = options.duration || 1500
      this.snackbar.message = options.message
      this.snackbar.show = true
    },
  },
  mounted() {
    // エントリー名絞込のトリガーです
    // 絞込テキストの入力があるとfilterTextSubjectに通知をするので、
    // 適度なタイミング（xxx ms入力が止まっている and 前回と値が違う）で
    // 実際の絞り込み処理を行います。
    const subscription = this.textSubject
      .asObservable()
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        map(text =>
          text
            .split('\n')
            .filter(v => v)
            .map(v => v.trim()),
        ),
        tap(codes => {
          this.fetching = true
          this.makerSearchCount = codes.length
        }),
        switchMap(codes => {
          if (codes.length === 0) {
            return of([])
          } else {
            return from(this.$maker.getByCode(codes))
          }
        }),
      )
      .subscribe(result => {
        this.noHits = result.length === 0
        this.makerFoundCount = result.length
        this.makers = result.slice(0, this.maxSearchCount)
        this.selected = []
        this.fetching = false
      })

    this.$on('hook:beforeDestroy', () => subscription.unsubscribe())

    // 入力欄にフォーカスを自動で当てたいが、ダイアログ内にこのコンポーネントを置いた場合、
    // 少し待たないとfocusが効かなかった（$elではちゃんとinput要素が取れていたが）。
    setTimeout(() => {
      this.$refs.text.$el.focus()
    }, 500)
  },
}
</script>

<style lang="scss" scoped>
.makers-container {
  position: relative;
  height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid var(--color-border);
  border-radius: 5px;
}

.message-max-count-exceeds {
  color: #ff7043; // TODO 色ちゃんとする
  width: 100%;
  text-align: center;
}

.fetching {
  background: rgba($color: #000000, $alpha: 0.2);
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 10;
}
</style>
